/*
 * Copyright (c) 2025, Gather
 * https://gather.life
 */

'use client';

import {
  Accordion as MuiAccordion,
  AccordionDetails as MuiAccordionDetails,
  AccordionSummary as MuiAccordionSummary,
  type AccordionSummaryProps as MuiAccordionSummaryProps,
  Grid2 as Grid,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { memo, useCallback, useMemo, useState } from 'react';

import { Icon } from '../icon';
import { Typography } from '../typography';

import type {
  AccordionContentProps,
  AccordionHeaderProps,
  AccordionProps,
} from './accordion.interface';
import { deepmerge } from '@mui/utils';

const AccordionSummary = styled(MuiAccordionSummary, {
  shouldForwardProp: (prop) => prop !== 'disableActions',
})<MuiAccordionSummaryProps>(({ theme, disableActions }) => ({
  padding: theme.spacing(5, 6),

  ...(disableActions && {
    '&:hover': {
      cursor: 'default !important',
    },
    '&.Mui-focusVisible': {
      backgroundColor: 'transparent',
    },
  }),
}));

const AccordionHeaderContainer = styled(
  (props: AccordionHeaderProps) => {
    const {
      children,
      padding,
      slotProps,
      slots,
      sx,
      verticalAlignment,
      verticalAlignmentAssignment = 'both',
      ...otherProps
    } = props;

    const mergedSlotProps = useMemo(() => {
      const shouldAlignStartAdornment =
        verticalAlignmentAssignment === 'start' || verticalAlignmentAssignment === 'both';

      if (!shouldAlignStartAdornment) {
        return slotProps;
      }

      return deepmerge(slotProps, {
        content: {
          sx: {
            alignItems: verticalAlignment,
          },
        },
      });
    }, [slotProps, verticalAlignment, verticalAlignmentAssignment]);

    const mergedSx = useMemo(() => {
      const shouldAlignEndAdornment =
        verticalAlignmentAssignment === 'end' || verticalAlignmentAssignment === 'both';
      const defaultSx = { ...sx, padding };

      if (!shouldAlignEndAdornment) {
        return defaultSx;
      }

      return {
        ...defaultSx,
        alignItems: verticalAlignment,
      };
    }, [padding, sx, verticalAlignment, verticalAlignmentAssignment]);

    return (
      <AccordionSummary slotProps={mergedSlotProps} slots={slots} sx={mergedSx} {...otherProps}>
        {children}
      </AccordionSummary>
    );
  },
  {
    name: 'Accordion',
    slot: 'header',
  }
)<AccordionHeaderProps>();

const AccordionContentContainer = styled(
  (props: AccordionContentProps) => {
    const { children, padding, sx, ...otherProps } = props;

    const mergedSx = useMemo(() => {
      return { ...sx, padding };
    }, [padding, sx]);

    return (
      <MuiAccordionDetails sx={mergedSx} {...otherProps}>
        {children}
      </MuiAccordionDetails>
    );
  },
  {
    name: 'Accordion',
    slot: 'content',
  }
)<AccordionContentProps>();

const Accordion = styled(
  ({
    children,
    contentPadding,
    customHeader,
    defaultExpanded,
    disableActions,
    endIcon,
    expanded: controlledExpanded,
    headerAlignment,
    headerPadding,
    headerSlotProps,
    headerSlots,
    headerSx,
    showExpandIcon = true,
    startIcon,
    subtitle,
    title,
    onChange,
    ...props
  }: AccordionProps) => {
    const isControlled = controlledExpanded !== undefined;
    const [internalExpanded, setInternalExpanded] = useState(defaultExpanded ?? false);
    const expanded = isControlled ? controlledExpanded : internalExpanded;

    const handleChange = useCallback(
      (syntheticEvent: React.SyntheticEvent, isExpanded: boolean): void => {
        if (disableActions) {
          return;
        }

        if (!isControlled) {
          setInternalExpanded(isExpanded);
        }

        onChange?.(syntheticEvent, isExpanded);
      },
      [disableActions, isControlled, onChange]
    );

    const icon = useMemo(() => {
      if (!showExpandIcon) {
        return null;
      }

      if (endIcon) {
        return endIcon;
      }

      return <Icon useStroke icon='caretDown' />;
    }, [endIcon, showExpandIcon]);

    return (
      <MuiAccordion
        defaultExpanded={!isControlled ? defaultExpanded : undefined}
        expanded={isControlled || disableActions ? expanded : undefined}
        onChange={handleChange}
        {...props}
      >
        <AccordionHeaderContainer
          disableActions={disableActions}
          expandIcon={icon}
          padding={headerPadding}
          slotProps={headerSlotProps}
          slots={headerSlots}
          sx={headerSx}
          verticalAlignment={headerAlignment}
        >
          {startIcon && startIcon}
          {customHeader && customHeader}
          <Grid>
            {title && (
              <Typography size='x-large' variant='label'>
                {title}
              </Typography>
            )}
            {subtitle && (
              <Typography color='text.secondary' size='small' variant='body'>
                {subtitle}
              </Typography>
            )}
          </Grid>
        </AccordionHeaderContainer>
        <AccordionContentContainer padding={contentPadding}>{children}</AccordionContentContainer>
      </MuiAccordion>
    );
  },
  {
    name: 'Accordion',
    slot: 'root',
  }
)<AccordionProps>();

export default memo(Accordion);
